import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface BeforeAfterImage {
  id: string;
  beforeImage: string;
  afterImage: string;
  alt?: string;
  description?: string;
}

interface BeforeAfterGalleryProps {
  images: BeforeAfterImage[];
  title?: string;
  activeButtonColor?: string;
}

const BeforeAfterGallery = ({
  images,
  title,
  activeButtonColor = 'bg-trhine-red-100'
}: BeforeAfterGalleryProps) => {
  const { t } = useTranslation();
  // Initialize state with all images showing 'before' view
  const [activeView, setActiveView] = useState<Record<string, 'before' | 'after'>>({});

  // Set all images to 'before' view on component mount
  useEffect(() => {
    const initialState: Record<string, 'before' | 'after'> = {};
    images.forEach(img => {
      initialState[img.id] = 'before';
    });
    setActiveView(initialState);
  }, [images]);

  // Toggle between before and after views when clicked
  const toggleView = (id: string) => {
    setActiveView(prev => ({
      ...prev,
      [id]: prev[id] === 'before' ? 'after' : 'before'
    }));
  };

  return (
    <section className="py-16 bg-black">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-white text-center mb-12">{title || t('common.beforeAfterTitle')}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.map((image) => (
            <div 
              key={image.id} 
              className="relative overflow-hidden rounded-lg cursor-pointer"
              onClick={() => toggleView(image.id)}
            >
              <div className="relative aspect-[4/3]">
                <img
                  src={activeView[image.id] === 'before' ? image.beforeImage : image.afterImage}
                  alt={image.alt || t('common.beforeAfterAlt')}
                  className="w-full h-full object-cover"
                />
                
                {/* Fixed Description in Top Corner */}
                {image.description && (
                  <div className="absolute top-2 left-2 max-w-[80%] bg-black bg-opacity-60 backdrop-blur-sm rounded p-2 shadow-md">
                    <p className="text-white text-xs font-medium leading-tight">{image.description}</p>
                  </div>
                )}
              </div>
              <div className="absolute bottom-0 left-0 right-0 flex">
                <div
                  className={`flex-1 py-2 text-center font-medium ${
                    activeView[image.id] === 'before' ? 'bg-white text-black' : 'bg-black bg-opacity-70 text-white'
                  }`}
                >
                  {t('common.before')}
                </div>
                <div
                  className={`flex-1 py-2 text-center text-white font-medium ${
                    activeView[image.id] === 'after' ? activeButtonColor : 'bg-black bg-opacity-70'
                  }`}
                >
                  {t('common.after')}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BeforeAfterGallery; 